import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarCheck,
  FaCampground,
  FaChalkboardTeacher,
  FaSave,
  FaSignOutAlt,
  FaUserFriends,
  FaUserShield,
  FaUsers,
} from "react-icons/fa";
import Swal from "sweetalert2";
import API from "../../api/api";
import { AuthContext } from "../../context/AuthContext";

const todayIST = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());
const coachStatuses = ["", "present", "absent", "late", "permission", "on_duty"];
const dailyStatuses = ["", "present", "absent"];

const sectionDefinitions = [
  { key: "attendants", label: "Player Attendants", icon: FaUserFriends },
  { key: "coaches", label: "Coaches", icon: FaChalkboardTeacher },
  { key: "ssa", label: "SSA Officials", icon: FaUsers },
  { key: "irps", label: "IRPs", icon: FaUserShield },
];

const statusLabel = (status) => status ? status.replace("_", " ") : "Not marked";

const ManagerAttendance = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [date, setDate] = useState(todayIST);
  const [activeSection, setActiveSection] = useState("attendants");
  const [staff, setStaff] = useState([]);
  const [parents, setParents] = useState([]);
  const [assignedScopes, setAssignedScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.camp.getPeopleAttendanceSheet({ camp_id: user?.camp_id || undefined, date });
      const data = response?.data || response;
      setStaff(data?.staff || []);
      setAssignedScopes(data?.assigned_scopes || []);
      try {
        const attendantResponse = await API.camp.getAttendantAthletes({ camp_id: user?.camp_id || undefined, date });
        const attendantData = attendantResponse?.data || attendantResponse;
        setParents(attendantData?.athletes || []);
      } catch {
        setParents(data?.parents || []);
      }
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.message || "Unable to load manager attendance.", "error");
    } finally {
      setLoading(false);
    }
  }, [date, user?.camp_id]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  const rowsBySection = useMemo(() => ({
    attendants: parents,
    coaches: staff.filter((row) => row.person_type === "coach"),
    ssa: staff.filter((row) => row.person_type === "ssa_official"),
    irps: staff.filter((row) => row.person_type === "irp"),
  }), [parents, staff]);

  const updateStaff = (type, id, field, value) => {
    setStaff((rows) => rows.map((row) => (
      row.person_type === type && row.person_id === id ? { ...row, [field]: value } : row
    )));
  };

  const updateParent = (id, field, value) => {
    setParents((rows) => rows.map((row) => row.athlete_id === id ? { ...row, [field]: value } : row));
  };

  const save = async () => {
    setSaving(true);
    try {
      const isAttendants = activeSection === "attendants";
      await API.camp.savePeopleAttendance({
        camp_id: user?.camp_id || undefined,
        date,
        parents: isAttendants ? rowsBySection.attendants : [],
        staff: isAttendants ? [] : rowsBySection[activeSection],
      });
      await Swal.fire({ icon: "success", title: "Attendance Saved", timer: 1300, showConfirmButton: false });
      fetchAttendance();
    } catch (error) {
      Swal.fire("Save Failed", error?.response?.data?.message || error?.message || "Unable to save attendance.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const personName = (row) => (
    <td className="p-3">
      <div className="font-semibold text-slate-900">{row.name}</div>
      <div className="text-[11px] text-slate-500">{row.subtitle || row.mobile || ""}</div>
    </td>
  );

  const renderDailyStaff = (rows, emptyMessage) => (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-600">
          <tr><th className="p-3 text-left">Name</th><th className="p-3">Status</th><th className="p-3 text-left">Remarks</th></tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.person_type}-${row.person_id}`} className="border-t border-slate-100">
              {personName(row)}
              <td className="p-3 text-center">
                <select
                  value={row.morning_status || ""}
                  onChange={(event) => updateStaff(row.person_type, row.person_id, "morning_status", event.target.value)}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm capitalize"
                >
                  {dailyStatuses.map((status) => <option key={status} value={status}>{statusLabel(status)}</option>)}
                </select>
              </td>
              <td className="p-3"><input value={row.remarks || ""} onChange={(event) => updateStaff(row.person_type, row.person_id, "remarks", event.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-slate-500">{emptyMessage}</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const renderCoaches = () => {
    const rows = rowsBySection.coaches;
    return <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-600">
          <tr><th className="p-3 text-left">Coach</th><th className="p-3">Morning</th><th className="p-3">Evening</th><th className="p-3 text-left">Remarks</th></tr>
        </thead>
        <tbody>
          {rows.map((row) => <tr key={`coach-${row.person_id}`} className="border-t border-slate-100">
            {personName(row)}
            {["morning_status", "evening_status"].map((field) => <td key={field} className="p-3 text-center"><select value={row[field] || ""} onChange={(event) => updateStaff("coach", row.person_id, field, event.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm capitalize">{coachStatuses.map((status) => <option key={status} value={status}>{statusLabel(status)}</option>)}</select></td>)}
            <td className="p-3"><input value={row.remarks || ""} onChange={(event) => updateStaff("coach", row.person_id, "remarks", event.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></td>
          </tr>)}
          {rows.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slate-500">No coaches are available in this camp.</td></tr>}
        </tbody>
      </table>
    </div>;
  };

  const renderAttendants = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-600">
          <tr><th className="p-3 text-left">Player</th><th className="p-3">Gender</th><th className="p-3">Attendants</th><th className="p-3">Status</th><th className="p-3 text-left">Remarks</th></tr>
        </thead>
        <tbody>
          {rowsBySection.attendants.map((row) => <tr key={row.athlete_id} className="border-t border-slate-100">
            <td className="p-3"><div className="font-semibold text-slate-900">{row.athlete_name}</div><div className="text-[11px] text-slate-500">{row.id_number || ""} {row.district ? `· ${row.district}` : ""}</div></td>
            <td className="p-3 text-center capitalize">{row.gender || "-"}</td>
            <td className="p-3"><div className="flex min-w-[210px] items-center justify-center gap-2">
              <select value={row.attendant_type || "none"} onChange={(event) => {
                const attendantType = event.target.value;
                const defaultCount = attendantType === "none" ? 0 : attendantType === "both" ? 2 : Math.max(1, Number(row.attendant_count) || 0);
                setParents((rows) => rows.map((parent) => parent.athlete_id === row.athlete_id ? { ...parent, attendant_type: attendantType, attendant_count: defaultCount, status: attendantType === "none" ? "absent" : parent.status } : parent));
              }} className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm">
                <option value="none">None</option><option value="father">Father</option><option value="mother">Mother</option><option value="both">Both parents</option><option value="other">Other</option>
              </select>
              <input type="number" min="0" max="10" value={row.attendant_count || 0} disabled={row.attendant_type === "none"} onChange={(event) => updateParent(row.athlete_id, "attendant_count", Number(event.target.value))} className="w-16 rounded-lg border border-slate-300 px-2 py-2 text-center disabled:bg-slate-100" title="Attendant count" />
            </div></td>
            <td className="p-3 text-center"><select value={row.status || ""} disabled={Number(row.attendant_count) === 0} onChange={(event) => updateParent(row.athlete_id, "status", event.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm capitalize disabled:bg-slate-100">{dailyStatuses.map((status) => <option key={status} value={status}>{statusLabel(status)}</option>)}</select></td>
            <td className="p-3"><input value={row.remarks || ""} disabled={Number(row.attendant_count) === 0} onChange={(event) => updateParent(row.athlete_id, "remarks", event.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-100" /></td>
          </tr>)}
          {rowsBySection.attendants.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">Player-attendant attendance has not been allotted to this manager.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const activeDefinition = sectionDefinitions.find((section) => section.key === activeSection);
  const hasAttendantAssignment = assignedScopes.includes("male_attendants") || assignedScopes.includes("female_attendants");

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-indigo-700 p-3 text-white"><FaUserShield /></div>
            <div><h1 className="font-bold text-slate-900">Camp Manager Portal</h1><p className="text-xs text-slate-500"><FaCampground className="mr-1 inline" /> Attendance management · {user?.name}</p></div>
          </div>
          <button type="button" onClick={handleLogout} className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700"><FaSignOutAlt /> Logout</button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-4 p-4 sm:p-6">
        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {sectionDefinitions.map(({ key, label, icon: Icon }) => {
            const isActive = activeSection === key;
            const isAllotted = key !== "attendants" || hasAttendantAssignment;
            return <button key={key} type="button" onClick={() => setActiveSection(key)} className={`rounded-2xl border p-4 text-left shadow-sm transition ${isActive ? "border-indigo-600 bg-indigo-700 text-white" : "border-slate-200 bg-white text-slate-800 hover:border-indigo-300"}`}>
              <div className="flex items-center justify-between"><Icon className={isActive ? "text-indigo-100" : "text-indigo-600"} /><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${isActive ? "bg-white/20" : "bg-slate-100"}`}>{rowsBySection[key].length}</span></div>
              <div className="mt-3 font-bold">{label}</div>
              {key === "attendants" && !isAllotted && <div className={`mt-1 text-[10px] ${isActive ? "text-indigo-100" : "text-amber-700"}`}>Not allotted</div>}
            </button>;
          })}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
            <div><h2 className="flex items-center gap-2 font-bold text-slate-900"><FaCalendarCheck className="text-emerald-600" /> {activeDefinition?.label}</h2><p className="mt-1 text-xs text-slate-500">{activeSection === "coaches" ? "Mark morning and evening attendance." : "Mark one attendance status for the selected date."}</p></div>
            <div className="flex items-center gap-2"><input type="date" value={date} onChange={(event) => setDate(event.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" /><button type="button" onClick={save} disabled={saving || loading || rowsBySection[activeSection].length === 0} className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-4 py-2 text-xs font-bold text-white disabled:opacity-50"><FaSave /> {saving ? "Saving..." : "Save"}</button></div>
          </div>
          {loading ? <div className="p-10 text-center text-sm text-slate-500">Loading attendance...</div> : <>
            {activeSection === "attendants" && renderAttendants()}
            {activeSection === "coaches" && renderCoaches()}
            {activeSection === "ssa" && renderDailyStaff(rowsBySection.ssa, assignedScopes.includes("ssa_officials") ? "No SSA officials are available in this camp." : "SSA officials attendance has not been allotted to this manager.")}
            {activeSection === "irps" && renderDailyStaff(rowsBySection.irps, "No IRPs are available in this camp.")}
          </>}
        </section>
      </main>
    </div>
  );
};

export default ManagerAttendance;
